import { useMemo } from "react";
import { useChatStore } from "@/stores/chat-store";
import type { DesignerPlatform, Platform } from "@/stores/chat-store";

export type OnboardingStage =
  | "new"
  | "setup-complete"
  | "active"
  | "power-user";

export function useOnboarding() {
  const onboardingProgress = useChatStore((s) => s.onboardingProgress);
  const markWizardComplete = useChatStore((s) => s.markWizardComplete);
  const setWizardStep = useChatStore((s) => s.setWizardStep);
  const setSelectedPlatform = useChatStore((s) => s.setSelectedPlatform);
  const setSelectedDesignerPlatform = useChatStore(
    (s) => s.setSelectedDesignerPlatform,
  );
  const markFlowComplete = useChatStore((s) => s.markFlowComplete);
  const markStepComplete = useChatStore((s) => s.markStepComplete);
  const isStepComplete = useChatStore((s) => s.isStepComplete);
  const updateOnboardingProgress = useChatStore(
    (s) => s.updateOnboardingProgress,
  );
  const resetOnboardingProgress = useChatStore(
    (s) => s.resetOnboardingProgress,
  );

  const derived = useMemo(() => {
    const isNewUser =
      !onboardingProgress.hasCompletedWizard && !onboardingProgress.dismissed;
    const isSetupComplete =
      onboardingProgress.hasCompletedWizard ||
      onboardingProgress.setupCompleted;

    let onboardingStage: OnboardingStage = "new";
    if (onboardingProgress.hasRunOrchestration) {
      onboardingStage = "power-user";
    } else if (
      onboardingProgress.hasCreatedRequest ||
      onboardingProgress.hasCreatedEpic
    ) {
      onboardingStage = "active";
    } else if (isSetupComplete) {
      onboardingStage = "setup-complete";
    }

    const isFlowComplete = (flowId: string): boolean =>
      onboardingProgress.completedFlows.includes(flowId);

    return {
      isNewUser,
      isSetupComplete,
      onboardingStage,
      currentStep: onboardingProgress.currentWizardStep,
      selectedPlatform: onboardingProgress.selectedPlatform,
      selectedDesignerPlatform: onboardingProgress.selectedDesignerPlatform,
      completedFlows: onboardingProgress.completedFlows,
      completedSteps: onboardingProgress.completedSteps,
      isFlowComplete,
    };
  }, [onboardingProgress]);

  return {
    ...derived,
    markWizardComplete,
    setWizardStep,
    setSelectedPlatform,
    setSelectedDesignerPlatform,
    markFlowComplete,
    markStepComplete,
    isStepComplete,
    updateOnboardingProgress,
    resetOnboardingProgress,
  };
}

export type { DesignerPlatform, Platform };
